import { withinScopeDates, type PointsScope } from '@brush/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { candidateHeader, candidateRows, emptyCandidatesHint, type ScopeSubject } from '../../leaderboards/candidates';
import type { CandidateList } from '../../leaderboards/candidateList';
import { TagFilterPicker } from '../TagFilterPicker';
import type { TagSelection } from '../../tagSelection';

/**
 * §9.9's "which events this leaderboard scores", as an operator actually works it: filter, then
 * tick.
 *
 * <b>Why this is not `ScopeEditor`.</b> The shared editor presents §4.2's document field by field —
 * a date range, a minimum field size, and two override lists of event ids. (Its tag control is the
 * same one-chip-per-tag picker as this file's, so the difference is the surrounding shape rather
 * than the gesture.) That is the right shape for the designer's *test* scope, where the subject is
 * the rule. It is the wrong shape here, where
 * the subject is the season: a room manager knows which nights count, and asking them to express
 * that as an include-list and an exclude-list of overrides is asking them to think like the schema.
 * So this screen filters to a candidate set and then lets them tick it, and the document is derived.
 *
 * <b>The mapping, which is the part worth not re-deriving.</b> Unticking a candidate writes it to
 * `excludeEventIds`; ticking it back removes it. Ticking is deliberately *not* `includeEventIds` —
 * that field is §3.2's override, which beats the date and tag rules rather than constituting the
 * set. Keeping the rules as the base and exclusions as the exceptions is what makes a running
 * series behave: a tournament added next week that matches the filters is scored automatically,
 * which is the whole reason a scope is a rule and not a list.
 *
 * <b>The quantifier differs from the tournaments list, and the chips do not say so on their own.</b>
 * There, a required tag is AND — a tournament must carry every one selected. Here §3.2 says an
 * event needs *at least one*. Same control, same gesture, different question, so the hint under it
 * states the rule outright, and the rows come from `candidateRows` (`leaderboards/candidates.ts`), which
 * matches tags as the server does and required tags any-of.
 *
 * <b>Where the rows come from</b> (known gap 35): the room's list, read page after page for the debounced
 * scope (`candidateList`) — every tournament in the scope's dates carrying a required tag, however many,
 * where it used to be the room's newest 200 whatever the dates. The live scope is applied to those rows here,
 * so narrowing shows at once and widening waits for the read; an unticked row stays listed, struck through.
 */
export function LeaderboardScopeFilters({
  scope,
  onChange,
  candidates,
  tags,
  readOnly,
  subject,
}: {
  /** The effective scope: dates already forced to the leaderboard's own. */
  scope: PointsScope;
  onChange: (next: PointsScope) => void;
  /** The candidate read for this scope (`candidateList`). */
  candidates: CandidateList;
  /** The tag vocabulary to offer. */
  tags: string[];
  readOnly: boolean;
  /** Which screen this is on, so the empty list names what that screen can widen (#545). */
  subject: ScopeSubject;
}) {
  const selection = useMemo<TagSelection>(
    () => ({ include: scope.includeTags, exclude: scope.excludeTags }),
    [scope.includeTags, scope.excludeTags],
  );

  // Open when it is doing something, so a board with a tag rule never hides it — and closed on a
  // plain date-range board, where it is one more thing between the operator and the list.
  const [tagsOpen, setTagsOpen] = useState(
    () => selection.include.length > 0 || selection.exclude.length > 0,
  );

  /**
   * The events the date range and the tag rules admit, soonest first, each saying whether it is unticked.
   *
   * The server stays the authority on the resulting count — it is the same code the recalculation runs — and the
   * two agree by construction now that the one rule the browser cannot evaluate, minimum field size, is not set on
   * this screen. The dates go through `withinScopeDates`, the shared package's one comparison: a null bound
   * compared inline is `false` in JavaScript, which drops the whole list.
   */
  const rows = useMemo(
    () => candidateRows(candidates.result?.rows ?? [], scope, (date) => withinScopeDates(date, scope)),
    [candidates.result, scope],
  );
  const candidateList = useMemo(() => rows.map((r) => r.tournament), [rows]);

  const excluded = useMemo(() => new Set(scope.excludeEventIds), [scope.excludeEventIds]);
  const chosen = candidateList.filter((t) => !excluded.has(t.id));
  const header = candidateHeader(candidates, chosen.length, candidateList.length);

  const setExcluded = (ids: Set<string>) =>
    onChange({ ...scope, excludeEventIds: [...ids] });

  const toggle = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcluded(next);
  };

  const selectAll = () => {
    // Only the candidates are cleared. An id excluded while some other filter was in force is left
    // alone rather than silently reinstated by a button about the events on screen.
    const next = new Set(excluded);
    for (const t of candidateList) next.delete(t.id);
    setExcluded(next);
  };

  const clearAll = () => {
    const next = new Set(excluded);
    for (const t of candidateList) next.add(t.id);
    setExcluded(next);
  };

  return (
    <div className="space-y-3">
      <div>
        <button
          type="button"
          onClick={() => setTagsOpen((x) => !x)}
          aria-expanded={tagsOpen}
          className="flex items-center gap-1 text-xs font-medium text-ink-800/70 hover:text-ink-950"
        >
          {tagsOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          Filter by tag
          <span className="ml-1 font-normal text-ink-800/45">{summarise(selection)}</span>
        </button>

        {tagsOpen ? (
          <div className="mt-2 border-l-2 border-ink-800/10 pl-3">
            {tags.length === 0 ? (
              <p className="text-[11.5px] text-ink-800/45">
                No tags have been used in this room yet.
              </p>
            ) : (
              <>
                <TagFilterPicker
                  tags={tags}
                  value={selection}
                  onChange={(next) =>
                    onChange({ ...scope, includeTags: next.include, excludeTags: next.exclude })
                  }
                />
                {/* Stated, never inferred from the chips: this is the one place the gesture means
                    something different from the tournaments list. */}
                <p className="mt-1.5 text-[11px] leading-relaxed text-ink-800/50">
                  Click once to require a tag, twice to exclude it.{' '}
                  {selection.include.length > 1
                    ? 'An event needs at least one of the required tags — not all of them.'
                    : 'Struck-through tags keep an event out whatever else it carries.'}
                </p>
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border border-ink-800/15">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-ink-800/10 px-3 py-2">
          {/* The count is of the rows on screen, which are one whole answer: the pages of a read arrive together,
              never one at a time, so this never counts a half-read list. While a newer read is due the rows
              stay and say so. */}
          <span className="text-xs text-ink-800/70">
            {header.kind === 'loading' ? (
              'Loading this room’s tournaments…'
            ) : header.kind === 'failed' ? (
              // No count over a list that never loaded (#224): the reason and Try again are below.
              <span className="text-ink-800/45">List not loaded</span>
            ) : (
              <>
                <strong className="num text-ink-950">{header.chosen}</strong> of {header.of} selected
                {candidates.updating ? <span className="ml-2 text-ink-800/45">Updating…</span> : null}
              </>
            )}
          </span>
          {!readOnly && candidateList.length > 0 ? (
            <span className="flex gap-3 text-[11px]">
              <button
                type="button"
                onClick={selectAll}
                disabled={chosen.length === candidateList.length}
                className="font-medium text-felt-700 hover:text-felt-900 disabled:cursor-default disabled:text-ink-800/30"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={chosen.length === 0}
                className="text-ink-800/60 hover:text-ink-950 disabled:cursor-default disabled:text-ink-800/30"
              >
                Clear
              </button>
            </span>
          ) : null}
        </div>

        {candidates.loading ? null : candidates.result === undefined ? (
          <p className="px-3 py-6 text-center text-[11.5px] leading-relaxed text-chip-500">
            {candidates.failure ?? 'This room’s tournaments could not be read.'}{' '}
            <button type="button" onClick={candidates.retry} className="underline underline-offset-2">
              Try again
            </button>
          </p>
        ) : candidateList.length === 0 ? (
          <p className="px-3 py-6 text-center text-[11.5px] leading-relaxed text-ink-800/50">
            No tournament in this room matches these dates and tags.
            <br />
            <span className="text-[11px]">{emptyCandidatesHint(subject)}</span>
          </p>
        ) : (
          <div className={`max-h-64 overflow-y-auto ${candidates.updating ? 'opacity-60' : ''}`}>
            {rows.map(({ tournament: t, unticked }) => {
              const on = !unticked;
              return (
                <label
                  key={t.id}
                  className={`flex items-start gap-2.5 border-b border-ink-800/5 px-3 py-2 last:border-b-0 ${
                    readOnly ? '' : 'cursor-pointer hover:bg-felt-50/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={readOnly}
                    onChange={() => toggle(t.id)}
                    className="mt-0.5 accent-[var(--color-felt-700)]"
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-[12.5px] ${on ? 'text-ink-950' : 'text-ink-800/40 line-through'}`}
                    >
                      <span className="num text-ink-800/50">{t.internalId}</span> {t.name}
                    </span>
                    <span className="num block text-[10.5px] text-ink-800/45">
                      {t.date} · ${t.buyIn.toLocaleString()}
                      {t.entries > 0 ? ` · ${t.entries} entries` : ' · no results'}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-wrap justify-end gap-1">
                    {t.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-ink-800/5 px-1.5 py-0.5 text-[9.5px] uppercase tracking-wide text-ink-800/55"
                      >
                        {tag}
                      </span>
                    ))}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Every page is read, so a short list is a failure and never a cap: one that read 200 of 300 says so
          rather than reading like the whole season. A read that failed after its first page keeps what it had. */}
      {candidates.result && !candidates.result.complete ? (
        <p className="text-[11px] text-gold-500">
          Only {candidates.result.rows.length} of {candidates.result.total} tournaments could be read, so the
          list above may be missing some.{' '}
          <button type="button" onClick={candidates.retry} className="underline underline-offset-2">
            Try again
          </button>
        </p>
      ) : candidates.failure && candidates.result ? (
        <p className="text-[11px] text-chip-500">
          {candidates.failure}{' '}
          <button type="button" onClick={candidates.retry} className="underline underline-offset-2">
            Try again
          </button>
        </p>
      ) : null}
    </div>
  );
}

/** What the collapsed tag row says it is doing, so closing it never hides a live filter. */
function summarise(selection: TagSelection): string {
  const parts: string[] = [];
  if (selection.include.length > 0) parts.push(selection.include.join(', '));
  if (selection.exclude.length > 0) parts.push(`not ${selection.exclude.join(', ')}`);
  return parts.length === 0 ? '· none' : `· ${parts.join(' · ')}`;
}
